import { useState } from 'react';
import { EmailItem as EmailItemType, Attachment } from '../../api/cases';
import {
  Send, CornerDownRight, Clock, Bot, Paperclip, Image as ImageIcon, ChevronRight, ChevronDown,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface EmailItemProps {
  email: EmailItemType;
  onImageClick?: (attachment: Attachment) => void;
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return 'Data non disponibile';
  const date = new Date(dateString);
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Adesso';
  if (diffMins < 60) return `${diffMins} min fa`;
  if (diffHours < 24) return `${diffHours}h fa`;
  if (diffDays < 7) return `${diffDays}g fa`;
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

export function EmailItem({ email, onImageClick }: EmailItemProps) {
  const [showFullBody, setShowFullBody] = useState(true);

  const isInbound = email.direction?.toUpperCase() === 'INBOUND';
  const directionIcon = isInbound ? <CornerDownRight className="size-3.5" /> : <Send className="size-3.5" />;
  const directionLabel = isInbound ? 'Ricevuta' : 'Inviata';
  const directionBg = isInbound ? 'bg-info/10 text-info' : 'bg-success/10 text-success';
  const borderColor = isInbound ? 'border-l-info/30' : 'border-l-success/30';

  const imageAttachments = email.attachments.filter(att => att.is_image);
  const otherAttachments = email.attachments.filter(att => !att.is_image);

  return (
    <Card className={`mail-message ${borderColor} p-4`}>
      {/* Header row */}
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <Badge variant="outline" className={directionBg}>
            {directionIcon}
            {directionLabel}
          </Badge>

          <span className="text-sm font-semibold text-foreground break-all">
            {isInbound ? email.from_email : (email.to_email?.[0] || email.from_email)}
          </span>

          {email.priority && (
            <Badge variant="outline" className={
              email.priority === 'urgent' ? 'bg-urgent/10 text-urgent border-urgent/20' :
              email.priority === 'high' ? 'bg-high/10 text-high border-high/20' :
              email.priority === 'medium' ? 'bg-medium/10 text-medium border-medium/20' :
              'bg-muted text-muted-foreground border-border'
            }>
              {email.priority.toUpperCase()}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="size-3" />
            {formatRelativeTime(email.received_at)}
          </span>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            {formatDateTime(email.received_at)}
          </span>
        </div>
      </div>

      {/* Subject */}
      {email.subject && (
        <p className="text-sm font-medium text-foreground mb-2 break-words">
          {email.subject}
        </p>
      )}

      {/* AI Summary */}
      {email.summary_ai && (
        <div className="mb-3 p-3 bg-primary/5 rounded-lg border border-primary/10">
          <div className="flex items-center gap-1.5 mb-1">
            <Bot className="size-3.5 text-primary" />
            <span className="text-xs font-semibold text-primary">Riassunto AI</span>
          </div>
          <p className="text-sm text-foreground/80 leading-relaxed break-words">
            {email.summary_ai}
          </p>
        </div>
      )}

      {/* Tags */}
      {email.tags && email.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {email.tags.map((tag, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              #{tag}
            </Badge>
          ))}
        </div>
      )}

      {/* Body - expandable */}
      <div>
        {!showFullBody ? (
          <Button variant="link" className="h-auto p-0 text-xs text-primary" onClick={() => setShowFullBody(true)}>
            <ChevronRight className="size-3.5 mr-1" />
            Mostra messaggio completo
          </Button>
        ) : (
          <div className="mt-2">
            <div className="p-3 bg-muted/30 rounded border border-border ">
              <pre className="text-sm text-foreground/80 whitespace-pre-wrap font-sans break-words">
                {email.body_raw}
              </pre>
            </div>
            <Button variant="link" className="h-auto p-0 text-xs text-muted-foreground mt-1" onClick={() => setShowFullBody(false)}>
              <ChevronDown className="size-3.5 mr-1" />
              Nascondi
            </Button>
          </div>
        )}
      </div>

      {/* Attachments */}
      {(imageAttachments.length > 0 || otherAttachments.length > 0) && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
            <Paperclip className="size-3.5" />
            Allegati ({email.attachments.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {imageAttachments.map((att) => (
              <Button
                key={att.id}
                variant="outline"
                size="sm"
                onClick={() => onImageClick?.(att)}
                className="break-all"
              >
                <ImageIcon className="size-4 shrink-0" />
                <span className="break-all">{att.filename}</span>
                {att.is_dicom && (
                  <Badge variant="destructive" className="ml-1 text-xs">DICOM</Badge>
                )}
              </Button>
            ))}
            {otherAttachments.map((att) => (
              <div
                key={att.id}
                className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border text-sm"
              >
                <Paperclip className="size-4 text-muted-foreground shrink-0" />
                <span className="break-all">{att.filename}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
